import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { resetPassword } from '../services/authService';
import './Auth.css';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match. Please verify.');
      return;
    }

    try {
      setLoading(true);
      const res = await resetPassword(token, password);
      if (res.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(res.message || 'Failed to reset password. The link may have expired.');
      }
    } catch (err) {
      setError(err.message || 'Invalid or expired password reset link. Please request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Set New Password | Durbhasi Gurukulam Courses</title>
        <meta name="description" content="Set a new password for your Durbhasi Gurukulam student account." />
      </Helmet>

      <div className="auth-page-wrapper">
        <div className="auth-card-container">
          {/* Left Brand Panel */}
          <div className="auth-brand-panel">
            <div>
              <div className="auth-brand-header">
                <img
                  src="/durbhasi-gurukulam-logo.png"
                  alt="Durbhasi Gurukulam"
                  className="auth-brand-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div>
                  <h1 className="auth-brand-title">Durbhasi <span>Gurukulam</span></h1>
                  <span className="auth-brand-tagline">Systems &amp; Software Engineering</span>
                </div>
              </div>

              <div className="auth-hero-copy">
                <h2>Set a Strong New Password</h2>
                <p>
                  Choose a secure password to protect your live cohort access, classroom attendance passes, and verified credentials.
                </p>

                <ul className="auth-perks-list">
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Instant Session Security Update</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Protected Student Credentials</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Seamless Single Sign-On Access</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="auth-brand-footer">
              🔒 256-Bit Encrypted Session • Durbhasi Gurukulam Portal
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="auth-form-panel">
            <span className="auth-form-kicker">Security Verification</span>
            <h2 className="auth-form-title">Create New Password</h2>
            <p className="auth-form-subtext">
              Please enter your new account password below.
            </p>

            {success ? (
              <div style={{ textAlign: 'center', padding: '30px 10px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
                <h3 style={{ color: '#065f46', marginBottom: '10px' }}>Password Successfully Updated!</h3>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '24px' }}>
                  Your password has been changed. You will be redirected to the sign-in page in a few seconds...
                </p>
                <Link to="/login" className="auth-submit-btn" style={{ display: 'inline-block', textDecoration: 'none' }}>
                  Sign In to Your Account Now →
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div className="auth-error-alert">
                    <span>⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="auth-form">
                  <div className="auth-input-group">
                    <label className="auth-input-label">
                      New Password <span>*</span>
                    </label>
                    <div className="auth-input-box">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        required
                        placeholder="At least 6 characters"
                        className="auth-input-field"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                        disabled={loading}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="auth-pw-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex="-1"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div className="auth-input-group">
                    <label className="auth-input-label">
                      Confirm New Password <span>*</span>
                    </label>
                    <div className="auth-input-box">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        required
                        placeholder="Re-enter your new password"
                        className="auth-input-field"
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(null); }}
                        disabled={loading}
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="auth-submit-btn"
                    disabled={loading}
                  >
                    {loading ? 'Updating Password...' : 'Save New Password & Sign In →'}
                  </button>
                </form>
              </>
            )}

            <div className="auth-card-footer">
              <span>Back to safety: </span>
              <Link to="/login">Sign In Instead</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ResetPassword;
